import React, { useState } from 'react'
import { AttorneyLayout } from '../components/AttorneyLayout'
import { PageHero } from '../components/PageHero'

// Contact — patterned directly after dewy-beatem-and-howe.com/contact.html
// ("Meet Della"): the AI intake demonstration content (greeting script, the
// two intake modes, what Della will/won't do), followed by a real contact
// form since this IS the attorney-facing site DB&H's page links out to.

const REASONS = ['General question', 'Intake AI early access', 'Advertising plans', 'Review Board application']
const EMPTY_FORM = { name: '', firm_name: '', email: '', phone: '', reason: REASONS[0], message: '', website: '' }

const WILL_DO = [
  'Explain what LeaseCheck collects and why',
  'Say which documents to have ready',
  "Describe how LeaseCheck's lease prescreening works on landlord-tenant matters",
  'Explain how a firm brings Della onto its own line',
  'Show how a firm can customize the workflow',
]

const WONT_DO = [
  'Say a caller "has a case" or predict an outcome',
  "Interpret a clause conclusively or apply law to a caller's specific facts",
  'Tell anyone to withhold rent or ignore a notice',
  'Claim confidentiality or an attorney-client relationship',
]

export function Contact() {
  const [form, setForm] = useState(EMPTY_FORM)
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const update = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }))

  async function submit(event) {
    event.preventDefault()
    setError('')
    if (!form.name.trim() || !form.firm_name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Complete all required fields before sending.')
      return
    }
    if (form.website) {
      setStatus('sent')
      return
    }
    setStatus('sending')
    try {
      const response = await fetch('/voice/contact', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      if (!response.ok) throw new Error('delivery failed')
      setStatus('sent')
    } catch {
      setStatus('idle')
      setError('We could not send your message. Please try again or use the email link below.')
    }
  }

  return (
    <AttorneyLayout>
      <PageHero
        eyebrow="The Intake AI Demonstration"
        title="Meet Della"
        body="Your most consistently available team member. Della is Intake AI, working under your firm's own name — she identifies the nature of a matter, collects the relevant facts, asks about notices and deadlines, identifies supporting documents, and prepares an organized summary for attorney review."
        image="/attorney/della.png"
        imageAlt="Illustration of Della, the AI intake assistant"
        belowTint
        primaryCta={{ label: 'Request Demo Access', to: '/contact#form' }}
      />

      {/* HOW DELLA ANSWERS THE PHONE — mirrors DB&H's contact.html ("Meet Della") page format */}
      <section id="demonstration" className="mx-auto max-w-4xl px-5 py-16 sm:py-20">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-6 sm:flex-row">
          <img src="/attorney/LeaseCheck%20Logo%20resized.png" alt="LeaseCheck" className="h-20 w-auto flex-none" />
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">How Della answers the phone</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">An intake assistant, not an AI lawyer.</h2>
          </div>
        </div>
        <blockquote className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-6 text-base italic leading-relaxed text-slate-700 sm:p-8">
          &ldquo;Thank you for calling Dewy, Beatem and Howe. I'm Della, the firm's AI intake assistant. I
          can answer general questions about the demonstration, help with a landlord-tenant or real-estate
          matter, or explain how LeaseCheck prepares information for attorney review. I am an AI assistant,
          not a lawyer so I can't provide legal advice. So, how can I help you today?&rdquo;
        </blockquote>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">Attorney Demonstration Mode</p>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">See Intake AI run under your firm's name.</h3>
            <p className="mt-2 text-sm text-slate-600">Tell Della you're an attorney and she demonstrates how Intake AI could handle an initial inquiry for your practice:</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {[
                'New-client screening and role identification',
                'Jurisdiction, property location, and lease type',
                'Notices, court deadlines, and documents available',
                'Matter urgency and consultation scheduling',
                'An automated Attorney Intake Brief, ready for the designated attorney and your practice-management system',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-emerald-500" />{item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">Property-Matter Mode</p>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">Experience the intake as a prospective client.</h3>
            <p className="mt-2 text-sm text-slate-600">Della identifies the role — tenant, landlord, property manager, buyer, seller, or owner — then asks only the questions relevant to that matter:</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {[
                'New lease or renewal, active or not',
                'State and county of the property',
                'Repairs, rent, fees, deposit, access, renewal, or termination',
                'Written notices received or served',
                'Court dates or response deadlines, and the documents on hand',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-emerald-500" />{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border-t-4 border-emerald-500 bg-white p-7 shadow-sm">
            <h3 className="text-base font-bold uppercase tracking-widest text-emerald-700">What Della will do</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              {WILL_DO.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <svg viewBox="0 0 20 20" aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none text-emerald-500" fill="currentColor">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7 7a1 1 0 0 1-1.4 0l-3-3a1 1 0 1 1 1.4-1.4l2.3 2.3 6.3-6.3a1 1 0 0 1 1.4 0z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-t-4 border-rose-400 bg-white p-7 shadow-sm">
            <h3 className="text-base font-bold uppercase tracking-widest text-rose-600">What Della won't do</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              {WONT_DO.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <svg viewBox="0 0 20 20" aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none text-rose-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 6l8 8M14 6l-8 8" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BRING LEASECHECK TO YOUR FIRM — mirrors DB&H's closing band; since this IS the attorney site, the CTA is the form below rather than an outbound link */}
      <section className="bg-slate-950 px-5 py-14 text-center text-white">
        <h2 className="text-2xl font-bold sm:text-3xl">This is Della, working under a fictional firm's name.</h2>
        <p className="mx-auto mt-3 max-w-xl text-white">She can work under yours.<br /> Tell us about your practice below, or reach us directly.</p>
      </section>

      {/* CONTACT FORM */}
      <section id="form" className="mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:py-20 lg:grid-cols-[1fr_20rem]">
        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
          {status === 'sent' ? <div className="py-16 text-center"><h2 className="text-3xl font-bold">Thank you.</h2><p className="mt-4 text-slate-600">Your message has been sent. We’ll be in touch.</p></div> : (
            <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2">
              <label className="text-sm font-semibold">Name<span aria-hidden="true"> *</span><input name="name" value={form.name} onChange={update} required maxLength="200" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal" /></label>
              <label className="text-sm font-semibold">Firm name<span aria-hidden="true"> *</span><input name="firm_name" value={form.firm_name} onChange={update} required maxLength="200" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal" /></label>
              <label className="text-sm font-semibold">Email<span aria-hidden="true"> *</span><input type="email" name="email" value={form.email} onChange={update} required maxLength="200" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal" /></label>
              <label className="text-sm font-semibold">Phone <span className="font-normal text-slate-500">(optional)</span><input type="tel" name="phone" value={form.phone} onChange={update} maxLength="200" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Reason<select name="reason" value={form.reason} onChange={update} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 font-normal">{REASONS.map((reason) => <option key={reason}>{reason}</option>)}</select></label>
              <label className="text-sm font-semibold sm:col-span-2">Message<span aria-hidden="true"> *</span><textarea name="message" value={form.message} onChange={update} required maxLength="2000" rows="6" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal" /></label>
              <label className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">Website<input name="website" value={form.website} onChange={update} tabIndex="-1" autoComplete="off" /></label>
              {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700 sm:col-span-2">{error}</p>}
              <button type="submit" disabled={status === 'sending'} className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 font-bold text-white disabled:opacity-60 sm:col-span-2">{status === 'sending' ? 'Sending…' : 'Send message'}</button>
            </form>
          )}
        </div>
        <aside className="rounded-2xl bg-slate-950 p-7 text-white"><img src="/attorney/LeaseCheck%20Logo%20resized.png" alt="LeaseCheck" className="mx-auto h-16 w-auto" /><h2 className="mt-5 text-xl font-bold">Prefer email?</h2><p className="mt-3 text-sm leading-relaxed text-white">If you prefer to email us directly use our email address below:</p><a href="mailto:attorneys@coloradoleasecheck.com" className="mt-5 inline-flex whitespace-nowrap text-sm font-semibold text-emerald-300">attorneys@coloradoleasecheck.com</a></aside>
      </section>
    </AttorneyLayout>
  )
}
